import React from "react";
import { Controller } from "react-hook-form";
import { formatCurrency as utilFormatCurrency } from "../../../../utils/format";

/**
 * ControlledField
 * A small wrapper around react-hook-form's Controller that standardizes
 * numeric input behavior: cleaning on change and formatting on blur.
 *
 * Props:
 * - name, control: react-hook-form Controller props
 * - InputComponent: component to render (FloatingInput or FloatingNumber)
 * - label, placeholder, type
 * - cleanNumber: function to clean live input (e.g., remove non-digits)
 * - handleCurrencyBlur: optional hook (field, raw) => formatted string
 * - onFormatted: optional callback(formatted) -- e.g., parent can sync state
 * - handleCalAmount: optional callback to run after blur
 */
export default function ControlledField({
  name,
  control,
  InputComponent,
  label,
  placeholder,
  type = "text",
  cleanNumber,
  handleCurrencyBlur,
  onFormatted,
  handleCalAmount,
  defaultValue = "",
  inputProps = {},
  disabled = false,
}) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <InputComponent
          {...field}
          placeholder={placeholder}
          label={label}
          type={type}
           disabled={disabled}  
          value={defaultValue}
          {...inputProps}
          onChange={(e) => {
             if (disabled) return;
            const raw = e?.target?.value ?? "";
            const cleaned = cleanNumber ? cleanNumber(raw) : raw;
            field.onChange(cleaned);
          }}
          onBlur={(e) => {
             if (disabled) return;
            // notify RHF
            field.onBlur?.(e);

            const rawValue = e?.target?.value ?? field.value ?? "";
            const cleaned = cleanNumber
              ? cleanNumber(String(rawValue))
              : String(rawValue).replace(/[^0-9.]/g, "");

            // If empty, skip formatting
            if (cleaned === "") return;

            let formatted = null;

            // Try currency blur function safely
            try {
              const maybe = handleCurrencyBlur
                ? handleCurrencyBlur(name, cleaned)
                : null;
              if (typeof maybe === "string" && maybe !== "") {
                formatted = maybe;
              }
            } catch {
              // ignore
            }

            // fallback
            if (!formatted) {
              formatted = utilFormatCurrency(cleaned);
            }

            // ✅ Only update if value actually changed (prevents re-render loop)
            if (formatted !== field.value) {
              field.onChange(formatted);
              onFormatted?.(name, formatted);
            }
          }}
        />
      )}
    />
  );
}
